import LinkButton from "@components/buttons/LinkButton"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import MuxVideo from "@components/MuxVideo"
import Video from "@components/Video"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import {
  body18Styles,
  h3Styles,
  h4Styles,
  h5Styles,
  paragraphStyles,
  smallcapsBold13Styles,
} from "@constants/typography"
import {
  DesktopOnly,
  MobileAndDesktopOnly,
  MobileAndTabletOnly,
  TabletOnly,
} from "@utils/MediaQuery"
import React from "react"
import styled from "styled-components"

const mediaProps = `
  position: relative;
  width: 100%;
  height: 501px;
  flex-grow: 0;
  flex-shrink: 0;

  @media ${breakpoints.tablet} {
    width: calc(10 / 12 * 100vw);
    height: calc(10 / 12 * 100vw * 0.70);
  }

  @media ${breakpoints.laptop} {
    width: calc(7 / 12 * 100vw);
    height: calc(7 / 12 * 100vw * 0.56);
    align-self: stretch;
  }

  @media ${breakpoints.desktop} {
    width: calc(8 / 12 * 100vw);
    height: calc(8 / 12 * 100vw * 0.56);
  }
`

const fiftyPercentMediaProps = `
  @media ${breakpoints.laptop} {
    width: calc(5 / 12 * 100vw);
    height: calc(5 / 12 * 100vw);
    align-self: stretch;
  }

  @media ${breakpoints.desktop} {
    width: calc(5 / 12 * 100vw);
    height: calc(5 / 12 * 100vw);
  }
`

const IFrame = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
`

const VideoWrapper = styled.div<{ fiftyPercentMedia?: boolean }>`
  ${mediaProps}
  ${({ fiftyPercentMedia }) =>
    fiftyPercentMedia ? fiftyPercentMediaProps : ""}
`

const ImageWrapper = styled.div<{ fiftyPercentMedia?: boolean }>`
  ${ImageHelperContainer} {
    ${mediaProps}
    ${({ fiftyPercentMedia }) =>
      fiftyPercentMedia ? fiftyPercentMediaProps : ""}
  }
`

const GifWrapper = styled.div<{ fiftyPercentMedia?: boolean }>`
  ${mediaProps}
  ${({ fiftyPercentMedia }) =>
    fiftyPercentMedia ? fiftyPercentMediaProps : ""}
`

const GifContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const GifPadding = styled.div`
  width: 100%;
  padding-bottom: 100%;
`

const Gif = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
`

const justificationMap = {
  bottom: "flex-end",
  center: "center",
  top: "flex-start",
}

const Container = styled.div<{ justification?: string; reversed?: boolean }>`
  display: flex;
  flex-direction: column;

  @media ${breakpoints.tablet} {
    padding: 0 calc(1 / 12 * 100vw);
    flex-direction: row;
    flex-wrap: wrap;
  }

  @media ${breakpoints.laptop} {
    padding: 0 calc(1 / 12 * 100vw);
    flex-direction: ${({ reversed }) => (reversed ? "row" : "row-reverse")};
    align-items: ${({ justification }) =>
      justificationMap[justification || "center"]};
    justify-content: space-between;
  }
`

const ContentContainer = styled.div<{ fiftyPercentMedia?: boolean }>`
  display: flex;
  padding: 70px 20px 42px;
  flex-grow: 0;
  flex-shrink: 0;
  flex-direction: column;

  @media ${breakpoints.tablet} {
    width: calc(8 / 12 * 100vw);
    margin-bottom: 40px;
    padding: 0;
    flex-direction: row;
  }

  @media ${breakpoints.laptop} {
    ${({ fiftyPercentMedia }) =>
      fiftyPercentMedia
        ? "width: calc(4.5 / 12 * 100vw);"
        : "width: calc(2.5 / 12 * 100vw);"}
    padding: 0;
  }

  @media ${breakpoints.desktop} {
    ${({ fiftyPercentMedia }) =>
      fiftyPercentMedia
        ? "width: calc(4.5 / 12 * 100vw);"
        : "width: calc(1.5 / 12 * 100vw);"}
  }
`

const largeTitleStyles = `
  @media ${breakpoints.laptop} {
    width: 64px;
    height: 64px;
  }

  @media ${breakpoints.desktop} {
    width: 96px;
    height: 96px;
    margin-bottom: 20px;
  }
`

const IconWrapper = styled.div<{ largeTitle?: boolean }>`
  ${ImageHelperContainer} {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;

    @media ${breakpoints.tablet} {
      margin-top: 12px;
      margin-bottom: 0;
    }

    @media ${breakpoints.laptop} {
      width: 56px;
      height: 56px;
      margin-bottom: 14px;
    }

    picture > img {
      background-color: transparent;
    }

    ${({ largeTitle }) => (largeTitle ? largeTitleStyles : "")}
  }
`

const TabletTitleWrapper = styled.div``

const TitleWrapper = styled.div`
  @media ${breakpoints.tablet} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`

const getTitleProps = (color?: string) => `
  ${smallcapsBold13Styles}
  margin: 0 0 12px;
  color: ${color ? `#${color}` : colors.black};
  line-height: 24px;
  letter-spacing: 0.5px;

  @media ${breakpoints.tablet} {
    width: calc(3 / 12 * 100vw);
    margin: 0 calc(1 / 12 * 100vw) 0 0;
  }

  @media ${breakpoints.laptop} {
    ${h5Styles}
    margin-bottom: 28px;
    color: ${color ? `#${color}` : colors.black};
  }
`

const LargeTitle = styled.h2<{ color?: string }>`
  ${({ color }) => getTitleProps(color)}
  ${h4Styles}
  margin-bottom: 24px;

  @media ${breakpoints.laptop} {
    margin-bottom: 24px;
    color: ${({ color }) => (color ? `#${color}` : colors.black)};
  }

  @media ${breakpoints.desktop} {
    ${h3Styles}
    margin-bottom: 38px;
    color: ${({ color }) => (color ? `#${color}` : colors.black)};
  }
`

const Title = styled.h4<{ color?: string }>`
  ${({ color }) => getTitleProps(color)}
`

const Description = styled.div<{ color?: string }>`
  ${paragraphStyles}
  color: ${({ color }) => (color ? `#${color}` : colors.mediumDarkGray)};

  @media ${breakpoints.tablet} {
    width: calc(4 / 12 * 100vw);
    align-self: flex-end;
  }

  @media ${breakpoints.laptop} {
    ${body18Styles}
    color: ${({ color }) => (color ? `#${color}` : colors.mediumDarkGray)};
  }
`

const Spacer = styled.div`
  height: 24px;

  @media ${breakpoints.laptop} {
    height: 27px;
  }
`

const Footer = styled.h4<{ color?: string }>`
  ${smallcapsBold13Styles}
  margin: 16px 0 0;
  color: ${({ color }) => (color ? `#${color}` : colors.black)};
  line-height: 24px;
  letter-spacing: 0.5px;

  @media ${breakpoints.tablet} {
    width: calc(3 / 12 * 100vw);
    margin: 32px calc(1 / 12 * 100vw) 0 0;
  }

  @media ${breakpoints.laptop} {
    ${h5Styles}
    margin-top: 36px;
    color: ${({ color }) => (color ? `#${color}` : colors.black)};
  }
`

const LinkWrapper = styled.div`
  margin: 28px 0;
`

interface HighlightCardProps {
  autoplayVideo?: boolean
  callToActionButtonLink?: string
  callToActionButtonText?: string
  callToActionLink?: LinkProps
  description: string
  descriptionColor?: string
  fiftyPercentMedia?: boolean
  footer?: string
  footerColor?: string
  icon?: string
  image?: string
  imageGif?: string
  justification?: string
  largeTitle?: boolean
  mobileImage?: string
  mobileImageGif?: string
  mobileVideoUrl?: string
  muxMobileVideo?: {
    thumbnailTime?: string
    title: string
    video: {
      assetId: string
      audioOnly: boolean
      created_at: number
      duration: number
      max_stored_frame_rate: number
      max_stored_resolution: string
      playbackId: string
      ratio: string
      ready: boolean
      version: number
    }
  }
  muxVideo?: {
    thumbnailTime?: string
    title: string
    video: {
      assetId: string
      audioOnly: boolean
      created_at: number
      duration: number
      max_stored_frame_rate: number
      max_stored_resolution: string
      playbackId: string
      ratio: string
      ready: boolean
      version: number
    }
  }
  reversed?: boolean
  title: string
  titleColor?: string
  videoThumbnail?: string
  videoUrl?: string
  wistiaEmbedCode?: string
  wistiaVideoId?: string
}

interface LinkProps {
  external?: boolean
  invert?: boolean
  link: string
  openNewTab?: boolean
  text: string
}

const HighlightCard: React.FC<HighlightCardProps> = ({
  autoplayVideo,
  callToActionButtonLink,
  callToActionButtonText,
  callToActionLink,
  description,
  descriptionColor,
  fiftyPercentMedia,
  footer,
  footerColor,
  icon,
  image,
  imageGif,
  justification,
  largeTitle,
  mobileImage,
  mobileImageGif,
  mobileVideoUrl,
  muxMobileVideo,
  muxVideo,
  reversed,
  title,
  titleColor,
  videoThumbnail,
  videoUrl,
  wistiaEmbedCode,
  wistiaVideoId,
}) => {
  if (
    !image &&
    !imageGif &&
    !videoUrl &&
    !wistiaEmbedCode &&
    !(muxVideo?.video || muxMobileVideo?.video)
  ) {
    return null
  }

  const renderGifComponent = (
    title: string,
    gifUrl?: string,
    fiftyPercentMedia?: boolean
  ) => (
    <GifWrapper fiftyPercentMedia={fiftyPercentMedia}>
      <GifContainer>
        <GifPadding />
        <Gif alt={title ?? "Product Highlight Gif"} src={gifUrl} />
      </GifContainer>
    </GifWrapper>
  )

  const renderGif = (title: string, fiftyPercentMedia?: boolean) => {
    return mobileImageGif ? (
      <>
        <MobileAndTabletOnly>
          {renderGifComponent(title, mobileImageGif, fiftyPercentMedia)}
        </MobileAndTabletOnly>
        <DesktopOnly>
          {renderGifComponent(title, imageGif, fiftyPercentMedia)}
        </DesktopOnly>
      </>
    ) : (
      renderGifComponent(title, imageGif)
    )
  }

  const renderImage = (title: string, fiftyPercentMedia?: boolean) => {
    return mobileImage ? (
      <>
        <MobileAndTabletOnly>
          <ImageWrapper fiftyPercentMedia={fiftyPercentMedia}>
            <ImageHelper alt={title} src={mobileImage} />
          </ImageWrapper>
        </MobileAndTabletOnly>
        <DesktopOnly>
          <ImageWrapper fiftyPercentMedia={fiftyPercentMedia}>
            <ImageHelper alt={title} src={image!} />
          </ImageWrapper>
        </DesktopOnly>
      </>
    ) : (
      <ImageWrapper fiftyPercentMedia={fiftyPercentMedia}>
        <ImageHelper alt={title} src={image!} />
      </ImageWrapper>
    )
  }

  const renderWistiaVideo = (title: string, fiftyPercentMedia?: boolean) => {
    const allowPolicies = [
      "accelerometer",
      "encrypted-media",
      "fullscreen",
      "gyroscope",
      "picture-in-picture",
    ]
    if (autoplayVideo) {
      allowPolicies.push("autoplay")
    }
    const allow = allowPolicies.join("; ")

    return (
      <VideoWrapper fiftyPercentMedia={fiftyPercentMedia}>
        {wistiaVideoId ? (
          <>
            <IFrame
              allow={allow}
              src={`https://fast.wistia.net/embed/iframe/${wistiaVideoId}?videoFoam=true`}
              title={title ?? "Product Highlight Video"}
            />
            <script
              async
              src="https://fast.wistia.net/assets/external/E-v1.js"
            ></script>
          </>
        ) : (
          <IFrame
            allow={allow}
            src={wistiaEmbedCode}
            title={title ?? "Product Highlight Video"}
          />
        )}
      </VideoWrapper>
    )
  }

  const renderVideo = (title: string, fiftyPercentMedia?: boolean) => {
    return (
      <VideoWrapper fiftyPercentMedia={fiftyPercentMedia}>
        <Video
          autoPlay={autoplayVideo}
          controls={true}
          loop={autoplayVideo}
          mobileSrc={mobileVideoUrl}
          muted={true}
          playsInline={true}
          poster={videoThumbnail}
          preload="auto"
          src={videoUrl}
          title={title ?? "Product Highlight Video"}
        />
      </VideoWrapper>
    )
  }

  const renderMuxVideo = (fiftyPercentMedia?: boolean) => {
    return (
      <VideoWrapper fiftyPercentMedia={fiftyPercentMedia}>
        <MuxVideo
          autoplayVideo={autoplayVideo}
          muxMobileVideo={muxMobileVideo}
          muxVideo={muxVideo}
        />
      </VideoWrapper>
    )
  }

  const renderMedia = (title: string) => {
    if (muxVideo?.video.playbackId || muxMobileVideo?.video.playbackId) {
      return renderMuxVideo(fiftyPercentMedia)
    } else if (wistiaVideoId || wistiaEmbedCode) {
      return renderWistiaVideo(title, fiftyPercentMedia)
    } else if (videoUrl) {
      return renderVideo(title, fiftyPercentMedia)
    } else if (imageGif) {
      return renderGif(title, fiftyPercentMedia)
    } else if (image) {
      return renderImage(title, fiftyPercentMedia)
    } else {
      return null
    }
  }

  const parsedDescription = (description && description.split("\n\n")) || [""]

  return (
    <Container justification={justification} reversed={reversed}>
      <ContentContainer fiftyPercentMedia={fiftyPercentMedia}>
        {icon && (
          <MobileAndDesktopOnly>
            <IconWrapper largeTitle={largeTitle}>
              <ImageHelper src={icon} />
            </IconWrapper>
          </MobileAndDesktopOnly>
        )}
        <TitleWrapper>
          <TabletTitleWrapper>
            {largeTitle ? (
              <LargeTitle color={titleColor}>{title}</LargeTitle>
            ) : (
              <Title color={titleColor}>{title}</Title>
            )}
            {icon && (
              <TabletOnly>
                <IconWrapper largeTitle={largeTitle}>
                  <ImageHelper src={icon} />
                </IconWrapper>
              </TabletOnly>
            )}
          </TabletTitleWrapper>
          <TabletOnly>
            {footer && <Footer color={footerColor}>{footer}</Footer>}
          </TabletOnly>
        </TitleWrapper>
        {parsedDescription.length > 0 &&
          parsedDescription.map((d, i) => (
            <React.Fragment key={d}>
              <Description color={descriptionColor}>{d}</Description>
              {i !== parsedDescription.length - 1 && <Spacer />}
              {callToActionButtonText && callToActionButtonLink && (
                <LinkWrapper>
                  <LinkButton
                    text={callToActionButtonText}
                    to={callToActionButtonLink}
                  />
                </LinkWrapper>
              )}
              {callToActionLink && (
                <LinkWrapper>
                  <LinkButton
                    external={callToActionLink.external}
                    target={callToActionLink.openNewTab ? "_blank" : undefined}
                    text={callToActionLink.text}
                    to={callToActionLink.link}
                  />
                </LinkWrapper>
              )}
            </React.Fragment>
          ))}
        <MobileAndDesktopOnly>
          {footer && <Footer color={footerColor}>{footer}</Footer>}
        </MobileAndDesktopOnly>
      </ContentContainer>
      {renderMedia(title)}
    </Container>
  )
}

export default HighlightCard
